// src/services/lib/sessionExpiry.js
//
// Decides whether a 401 means "your session died" or "that credential was
// wrong". Extracted from the axios interceptor so it can be tested without
// importing services/api.js, which pulls in axios (ESM that this repo's Jest
// config cannot parse).
//
// Background: the user JWT lives 24h (backend/app/services/auth.py:26). When
// it lapses the backend answers 401 "Could not validate credentials". That
// used to fall through to every caller's own error copy — the Manager Portal
// rendered "Couldn't load your overview — check your connection and try
// again", which is the wrong instruction. The network is fine, Retry can never
// succeed, and the only fix is to sign in again. A pilot tenant sat on that
// screen for a day (reported 2026-08-27).
//
// The risk in fixing it is over-reach. Several endpoints answer 401 BY DESIGN
// when the credential supplied is wrong, and they are checked against a
// session that is still perfectly valid:
//
//   /manager-approvals/verify   ManagerGate — validate-and-discard. If a
//                               mistyped manager PIN expired the session, one
//                               wrong digit would log a till out mid-shift.
//   /auth/employee-login        the operator lock screen's PIN.
//   /auth/tenant-login, /auth/login   sign-in itself.
//
// Treating any of those as expiry would be worse than the original bug.
export const CREDENTIAL_CHECK_PATHS = [
    '/auth/login',
    '/auth/tenant-login',
    '/auth/employee-login',
    '/manager-approvals/verify',
];

/**
 * @param {string} url            the failing request's URL
 * @param {boolean} hasStoredToken whether a session token is present
 * @returns {boolean} true when this 401 should end the session
 */
export function isSessionExpiry(url, hasStoredToken) {
    // No stored token means there is no session to expire — a 401 here is a
    // failed sign-in, not a lapsed one.
    if (!hasStoredToken) return false;
    const path = url || '';
    return !CREDENTIAL_CHECK_PATHS.some((p) => path.includes(p));
}

export default isSessionExpiry;
